import { useMemo, useState } from 'react';
import type { DuplicateMatch, ImportDuplicateFlag } from '../../../../types/api';
import { Button } from '../../../../components/Button';
import {
  buildOverwriteFields,
  computeImportConflicts,
  computeImportFills,
  DEFAULT_IMPORT_DECISION,
  displayValue,
  type ConflictChoice,
  type ConflictChoices,
  type ImportDecision,
} from './importConflicts';
import './DuplicateImportGuard.css';

export type { ImportDecision } from './importConflicts';

/**
 * One flagged row awaiting a decision. `id` is opaque to the guard and comes back
 * on the resolution, so a caller importing several teams at once can key rows by
 * team and row index without the guard knowing anything about teams.
 */
export type GuardItem = {
  id: string;
  /** Team name, shown when the batch spans more than one team. */
  groupLabel?: string;
  flag: ImportDuplicateFlag;
};

/** What the caller acts on once the coach confirms. */
export type ImportResolution = {
  decision: ImportDecision;
  /** The existing player this row merges into. Only set when `decision` is `merge`. */
  matchProspectId?: string;
  /** Fields where the roster's value should replace the stored one. */
  overwriteFields: string[];
};

interface DuplicateImportGuardProps {
  items: GuardItem[];
  /** Apply the per-row decisions, keyed by {@link GuardItem.id}. */
  onConfirm: (resolutions: Record<string, ImportResolution>) => void;
  /** Go back without committing anything. */
  onCancel: () => void;
  /** Disables controls while the parent commits the import. */
  isSubmitting?: boolean;
  /**
   * Fields to leave out of the diff entirely — no conflict question, no auto-fill line.
   *
   * The roster import passes the JUCO toggle's two fields when the toggle is on: the coach has
   * already said what those should be, so a stored value that disagrees is not a question, and
   * asking on every row of every re-import would read as the toggle not working.
   */
  suppressedFields?: readonly string[];
}

/** Human labels for the `matchedOn` codes the backend returns. */
const MATCHED_ON_LABELS: Record<string, string> = {
  name: 'name',
  gradYear: 'grad year',
  position: 'position',
  team: 'team',
};

function formatMatch(match: DuplicateMatch): string {
  const parts = [`${match.firstName} ${match.lastName}`.trim()];
  if (match.gradYear) parts.push(`Class of ${match.gradYear}`);
  if (match.position) parts.push(match.position);
  return parts.join(' · ');
}

/** Per-row UI state: what to do, which match, and how each conflict was resolved. */
type RowState = {
  decision: ImportDecision;
  matchProspectId: string;
  choices: ConflictChoices;
};

/**
 * Pre-import guard for roster rows that look like players the organization
 * already has.
 *
 * The default is to add the existing player to the team being imported and merge
 * the row into their record: blank fields are filled in from the roster, and any
 * field where the two disagree is listed so the coach can pick a side. The stored
 * value stays selected unless they change it. Importing as a separate record and
 * leaving the row out are both still available per player.
 */
export function DuplicateImportGuard({
  items,
  onConfirm,
  onCancel,
  isSubmitting = false,
  suppressedFields,
}: DuplicateImportGuardProps) {
  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const initial: Record<string, RowState> = {};
    for (const item of items) {
      initial[item.id] = {
        decision: DEFAULT_IMPORT_DECISION,
        // Best-corroborated match first, so the obvious pairing is preselected.
        matchProspectId: item.flag.matches[0]?.prospectId ?? '',
        choices: {},
      };
    }
    return initial;
  });

  const showGroups = items.some((item) => item.groupLabel);

  const updateRow = (id: string, patch: Partial<RowState>) => {
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const setChoice = (id: string, field: string, choice: ConflictChoice) => {
    setRows((prev) => ({
      ...prev,
      [id]: { ...prev[id], choices: { ...prev[id].choices, [field]: choice } },
    }));
  };

  /**
   * Conflicts and auto-fills recomputed per row against the currently selected
   * match, since switching matches changes what disagrees.
   */
  const diffs = useMemo(() => {
    const byId: Record<
      string,
      {
        match: DuplicateMatch | undefined;
        conflicts: ReturnType<typeof computeImportConflicts>;
        fills: ReturnType<typeof computeImportFills>;
      }
    > = {};

    for (const item of items) {
      const row = rows[item.id];
      const match = item.flag.matches.find((m) => m.prospectId === row?.matchProspectId);
      byId[item.id] = {
        match,
        conflicts: match
          ? computeImportConflicts(item.flag.candidate, match, { suppressedFields })
          : [],
        fills: match ? computeImportFills(item.flag.candidate, match, { suppressedFields }) : [],
      };
    }

    return byId;
  }, [items, rows, suppressedFields]);

  const handleConfirm = () => {
    const resolutions: Record<string, ImportResolution> = {};

    for (const item of items) {
      const row = rows[item.id];
      const decision = row?.decision ?? DEFAULT_IMPORT_DECISION;

      // A merge with nothing to merge into cannot be honored. That needs a flag
      // with no matches, which the backend does not produce, but importing the
      // row as its own record is the outcome that loses no data if it ever does.
      if (decision !== 'merge' || !row?.matchProspectId) {
        resolutions[item.id] = {
          decision: decision === 'merge' ? 'import_as_new' : decision,
          overwriteFields: [],
        };
        continue;
      }

      resolutions[item.id] = {
        decision,
        matchProspectId: row.matchProspectId,
        overwriteFields: buildOverwriteFields(diffs[item.id].conflicts, row.choices),
      };
    }

    onConfirm(resolutions);
  };

  const count = items.length;

  return (
    <div className="duplicate-import-guard" data-testid="duplicate-import-guard">
      <div className="duplicate-import-guard-header">
        <h3>You already have {count === 1 ? 'this player' : 'these players'}</h3>
        <p className="duplicate-import-guard-subtitle">
          {count} player{count !== 1 ? 's' : ''} on this roster {count !== 1 ? 'match' : 'matches'}{' '}
          someone you already have. By default we add them to the team and fill in anything new
          from the roster, instead of creating a second record. Where the roster disagrees with
          what's on file, pick which value to keep. Everyone not listed here imports normally.
        </p>
      </div>

      <ul className="duplicate-import-guard-list">
        {items.map((item) => {
          const row = rows[item.id];
          const decision = row?.decision ?? DEFAULT_IMPORT_DECISION;
          const { match, conflicts, fills } = diffs[item.id];
          const groupName = `import-decision-${item.id}`;
          const candidate = item.flag.candidate;

          return (
            <li
              key={item.id}
              className="duplicate-import-guard-item"
              data-testid={`guard-flag-${item.id}`}
            >
              <div className="guard-candidate">
                <span className="guard-candidate-label">
                  From this roster
                  {showGroups && item.groupLabel ? ` · ${item.groupLabel}` : ''}
                </span>
                <span className="guard-candidate-name">
                  {`${candidate.firstName} ${candidate.lastName}`.trim()}
                  {candidate.gradYear ? ` · Class of ${candidate.gradYear}` : ''}
                  {candidate.position ? ` · ${candidate.position}` : ''}
                </span>
              </div>

              <div className="guard-matches">
                <span className="guard-matches-label">
                  {item.flag.matches.length !== 1
                    ? 'Which player is this?'
                    : 'Already in your roster'}
                </span>
                {item.flag.matches.map((m) => (
                  <label key={m.prospectId} className="guard-match">
                    {item.flag.matches.length > 1 && (
                      <input
                        type="radio"
                        name={`${groupName}-match`}
                        value={m.prospectId}
                        checked={row?.matchProspectId === m.prospectId}
                        onChange={() => updateRow(item.id, { matchProspectId: m.prospectId })}
                        disabled={isSubmitting || decision !== 'merge'}
                        data-testid={`guard-match-${item.id}-${m.prospectId}`}
                      />
                    )}
                    <span className="guard-match-name">{formatMatch(m)}</span>
                    {m.teams.length > 0 && <span className="guard-match-teams">{m.teams.join(', ')}</span>}
                    <span className="guard-match-chips">
                      {m.matchedOn.map((code) => (
                        <span key={code} className="guard-chip">
                          {MATCHED_ON_LABELS[code] ?? code}
                        </span>
                      ))}
                    </span>
                  </label>
                ))}
              </div>

              {decision === 'merge' && match && (fills.length > 0 || conflicts.length > 0) && (
                <div className="guard-merge-preview">
                  {fills.length > 0 && (
                    <p className="guard-merge-fills" data-testid={`guard-fills-${item.id}`}>
                      Adding from this roster:{' '}
                      {fills.map((f) => `${f.label} (${displayValue(f.value)})`).join(', ')}
                    </p>
                  )}

                  {conflicts.length > 0 && (
                    <div className="guard-conflicts">
                      <span className="guard-conflicts-label">
                        {conflicts.length} field{conflicts.length !== 1 ? 's' : ''} disagree
                        {conflicts.length === 1 ? 's' : ''}
                      </span>
                      {conflicts.map((conflict) => {
                        const choice = row.choices[conflict.key] ?? 'existing';
                        const conflictGroup = `${groupName}-${conflict.key}`;
                        return (
                          <div
                            key={conflict.key}
                            className="guard-conflict"
                            data-testid={`guard-conflict-${item.id}-${conflict.key}`}
                          >
                            <span className="guard-conflict-label">{conflict.label}</span>
                            <div className="guard-conflict-options">
                              <label className="guard-conflict-option">
                                <input
                                  type="radio"
                                  name={conflictGroup}
                                  value="existing"
                                  checked={choice === 'existing'}
                                  onChange={() => setChoice(item.id, conflict.key, 'existing')}
                                  disabled={isSubmitting}
                                  data-testid={`guard-keep-${item.id}-${conflict.key}`}
                                />
                                <span>
                                  <span className="guard-conflict-source">Keep</span>{' '}
                                  {displayValue(conflict.existingValue)}
                                </span>
                              </label>
                              <label className="guard-conflict-option">
                                <input
                                  type="radio"
                                  name={conflictGroup}
                                  value="incoming"
                                  checked={choice === 'incoming'}
                                  onChange={() => setChoice(item.id, conflict.key, 'incoming')}
                                  disabled={isSubmitting}
                                  data-testid={`guard-take-${item.id}-${conflict.key}`}
                                />
                                <span>
                                  <span className="guard-conflict-source">Use roster</span>{' '}
                                  {displayValue(conflict.incomingValue)}
                                </span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <fieldset className="guard-choice">
                <legend className="guard-choice-legend">What should we do?</legend>
                <label className="guard-choice-option">
                  <input
                    type="radio"
                    name={groupName}
                    value="merge"
                    checked={decision === 'merge'}
                    onChange={() => updateRow(item.id, { decision: 'merge' })}
                    disabled={isSubmitting || item.flag.matches.length === 0}
                    data-testid={`guard-merge-${item.id}`}
                  />
                  <span>
                    <strong>Add to this team</strong>, using the player you already have
                  </span>
                </label>
                <label className="guard-choice-option">
                  <input
                    type="radio"
                    name={groupName}
                    value="import_as_new"
                    checked={decision === 'import_as_new'}
                    onChange={() => updateRow(item.id, { decision: 'import_as_new' })}
                    disabled={isSubmitting}
                    data-testid={`guard-import-${item.id}`}
                  />
                  <span>
                    <strong>Import as new</strong>, this is a different player
                  </span>
                </label>
                <label className="guard-choice-option">
                  <input
                    type="radio"
                    name={groupName}
                    value="skip"
                    checked={decision === 'skip'}
                    onChange={() => updateRow(item.id, { decision: 'skip' })}
                    disabled={isSubmitting}
                    data-testid={`guard-skip-${item.id}`}
                  />
                  <span>
                    <strong>Skip</strong>, leave this row out
                  </span>
                </label>
              </fieldset>
            </li>
          );
        })}
      </ul>

      <div className="button-group">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting} data-testid="guard-back-button">
          Back
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isSubmitting}
          data-testid="guard-continue-button"
        >
          {isSubmitting ? (
            <span className="button-content">
              <span className="spinner-small" />
              Importing...
            </span>
          ) : (
            'Continue import'
          )}
        </Button>
      </div>
    </div>
  );
}
